import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/packages', label: 'Packages' },
    { path: '/reviews', label: 'Reviews' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center group">
              <img 
                src="/serene-sparkle-logo.png" 
                alt="Serene Sparkle" 
                className="h-32 w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>

            <nav className="hidden md:flex items-center gap-6">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-sky-600 ${
                    location.pathname === link.path
                      ? 'text-sky-600'
                      : 'text-slate-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/admin">
                <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                  Admin
                </Button>
              </Link>
              <Link to="/track">
                <Button variant="outline" size="sm" className="border-sky-600 text-sky-600 hover:bg-sky-50">
                  Track Quote
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-sky-600 hover:bg-sky-700 text-white transition-colors">
                  Get a Quote
                </Button>
              </Link>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Link to="/contact">
                <Button size="sm" className="bg-sky-600 hover:bg-sky-700">
                  Quote
                </Button>
              </Link>
            </div>
          </div>

          {/* Mobile navigation */}
          <nav className="md:hidden mt-4 flex flex-wrap gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-sky-600'
                    : 'text-slate-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-slate-50 to-sky-50 border-t border-sky-100 mt-16">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="/serene-sparkle-logo.png" 
                alt="Serene Sparkle - Professional Cleaning Services" 
                className="h-24 w-auto object-contain mb-4"
                loading="lazy"
                width="200"
                height="96"
              />
              <p className="text-sm text-slate-600">
                Professional cleaning services creating peaceful, spotless spaces.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-sm text-slate-600 hover:text-sky-600 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/admin"
                    className="text-sm text-slate-600 hover:text-sky-600 transition-colors"
                  >
                    Admin Dashboard
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li><Link to="/services/house-cleaning" className="hover:text-sky-600 transition-colors">House Cleaning Services</Link></li>
                <li><Link to="/services/commercial-cleaning" className="hover:text-sky-600 transition-colors">Commercial Cleaning Services</Link></li>
                <li><Link to="/services/specialist-cleaning" className="hover:text-sky-600 transition-colors">Specialist Cleaning Services</Link></li>
                <li><Link to="/packages" className="hover:text-sky-600 transition-colors">View Cleaning Packages</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-slate-800 mb-4">Contact</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Phone className="h-4 w-4 mt-0.5 text-sky-600" />
                  <span>07951488263</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <Mail className="h-4 w-4 mt-0.5 text-sky-600" />
                  <span>Team@serene-world.com</span>
                </li>
                <li className="flex items-start gap-2 text-sm text-slate-600">
                  <MapPin className="h-4 w-4 mt-0.5 text-sky-600" />
                  <span>London, United Kingdom</span>
                </li>
              </ul>

              <h4 className="font-semibold text-slate-800 mt-6 mb-3">Follow Us</h4>
              <div className="flex gap-3">
                <a
                  href="https://www.facebook.com/profile.php?id=1134771913061431"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-sky-100 hover:bg-sky-200 p-2.5 rounded-full transition-colors"
                  data-testid="social-facebook"
                >
                  <Facebook className="h-5 w-5 text-sky-600" />
                </a>
                <a
                  href="https://www.instagram.com/sere.ne7309"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-sky-100 hover:bg-sky-200 p-2.5 rounded-full transition-colors"
                  data-testid="social-instagram"
                >
                  <Instagram className="h-5 w-5 text-sky-600" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-sky-100 mt-8 pt-8 text-center text-sm text-slate-600">
            <p>&copy; {new Date().getFullYear()} Serene Sparkle. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
